// ---------------------------------------------------------------------------
// Editor do layout de impressão + preview ao vivo.
//
// O que se edita aqui é DADO: blocos, campos, alinhamento, negrito, papel,
// colunas. Nada disso é código — vai para acd."ActLayout"/"ActLayoutInfo".
//
// NÃO desce sozinho para o PDV: não há fila de configuração para o layout (a
// rota grava direto na Central). Por isso os controles que prometeriam essa
// descida — "aplicar agora / no próximo período" e "imprimir teste" — estão
// desligados com o motivo escrito ao lado, em vez de escondidos.
//
// O preview à direita usa o MESMO renderizador do alfredo-ops-backend, então
// o que aparece na tela é o que sai na bobina.
// ---------------------------------------------------------------------------

import React, { useMemo, useState } from "react";
import PreviewComanda from "./PreviewComanda";
import {
  Alinhamento,
  BlocoComanda,
  CampoComanda,
  DocumentoComanda,
  LayoutComanda,
  layoutPadraoConferencia,
  layoutPadraoProducao,
  pedidoExemploDelivery,
  pedidoExemploMesa,
} from "../../../../comum/layout-comanda";

interface EditorLayoutComandaProps {
  /** Layout já gravado. Sem ele, começa do padrão do sistema. */
  layoutInicial?: LayoutComanda;
  /**
   * Devolve `true` quando gravou de verdade. É por essa resposta que o editor
   * sabe que pode limpar o "você tem alterações não salvas" — sem ela, um erro
   * do servidor deixaria o aviso sumir como se tivesse dado certo.
   */
  onSalvar?: (
    layout: LayoutComanda,
    aplicarEm: AplicarEm
  ) => void | boolean | Promise<void | boolean>;
  onFechar?: () => void;
  /**
   * Quando existe, trocar de documento é responsabilidade de quem monta a tela:
   * ele busca o layout GRAVADO daquele documento e devolve por `layoutInicial`.
   * Sem este callback (uso solto, como em ferramentas/gerarPreviewEstatico.tsx)
   * o editor cai no padrão do sistema, que é tudo que ele tem sem servidor.
   */
  onTrocarDocumento?: (documento: DocumentoComanda) => void;
  /** Salvamento em curso lá fora — trava o botão. */
  salvando?: boolean;
  /**
   * Dentro do painel da Configuração. O editor nasceu como PÁGINA (100vh e
   * fundo próprio); encaixado, quem manda no fundo e na altura é o painel.
   */
  encaixado?: boolean;
}

type AplicarEm = "agora" | "proximoPeriodo";

/**
 * Papéis suportados. As colunas NÃO saem daqui: `larguraColunas` é campo do
 * layout, editável ao lado, porque 56 colunas em 80mm é o que a via de produção
 * usa em produção hoje (printer.service.ts do OPS abre a impressora com
 * width: 56). Derivar a largura do papel apagava esse valor sem avisar.
 */
const PAPEIS: { valor: "80" | "58"; rotulo: string }[] = [
  { valor: "80", rotulo: "80 mm" },
  { valor: "58", rotulo: "58 mm" },
];

/** Limites do campo de colunas: fora disso o preview vira nada ou vira sopa. */
const COLUNAS_MINIMO = 16;
const COLUNAS_MAXIMO = 80;

const ROTULO_DO_ALINHAMENTO: Record<Alinhamento, string> = {
  esquerda: "Esquerda",
  centro: "Centro",
  direita: "Direita",
};

const NOME_DO_CAMPO: Record<string, string> = {
  filial: "Nome da filial",
  codigoPedido: "Número do pedido",
  tituloDocumento: "Título do documento",
  data: "Data e hora",
  periodo: "Período",
  caixa: "Caixa",
  operador: "Operador",
  entregador: "Entregador",
  numeroPessoas: "Número de pessoas",
  identificacaoItem: "Mesa / comanda / delivery",
  lugarOuContaInteira: "Lugar ou conta inteira",
  clienteNome: "Nome do cliente",
  clienteTelefone: "Telefone do cliente",
  clienteEndereco: "Endereço",
  clienteComplemento: "Complemento",
  clienteObservacao: "Observação do endereço",
  totalProdutos: "Total de produtos",
  descontoConta: "Desconto da conta",
  descontoProdutos: "Desconto de produtos",
  subtotal: "Subtotal",
  taxaServico: "Taxa de serviço",
  taxaEntrada: "Entrada",
  consumoMinimo: "Consumo mínimo",
  taxaEstacionamento: "Estacionamento",
  taxaEntrega: "Taxa de entrega",
  total: "Total",
  pagamentos: "Formas de pagamento",
  troco: "Troco",
  outrasImpressoes: "Outras impressões",
  permanencia: "Permanência",
  totalPorPessoa: "Total por pessoa",
  textoFixo: "Texto fixo",
  linhaEmBranco: "Linha em branco",
  separador: "Linha separadora",
  assinatura: "Assinatura",
  avisoNaoFiscal: "Aviso de não fiscal",
};

function nomeDoCampo(campo: CampoComanda): string {
  if (campo.chave === "textoFixo" && campo.texto) return `"${campo.texto}"`;
  return NOME_DO_CAMPO[campo.chave] ?? campo.chave;
}

const EditorLayoutComanda: React.FC<EditorLayoutComandaProps> = ({
  layoutInicial,
  onSalvar,
  onFechar,
  onTrocarDocumento,
  salvando,
  encaixado,
}) => {
  const [layout, setLayout] = useState<LayoutComanda>(
    layoutInicial ?? layoutPadraoProducao()
  );
  const [exemplo, setExemplo] = useState<"mesa" | "delivery">("mesa");
  const [aplicarEm, setAplicarEm] = useState<AplicarEm>("proximoPeriodo");
  /** Houve edição depois da última gravação. */
  const [sujo, setSujo] = useState(false);
  /** Troca de documento pedida e ainda não confirmada. */
  const [trocaPendente, setTrocaPendente] = useState<DocumentoComanda | null>(
    null
  );
  /** O que está escrito no campo de colunas enquanto se digita (null = o do layout). */
  const [colunasDigitadas, setColunasDigitadas] = useState<string | null>(null);

  // Papel e colunas moram DENTRO do layout, não em estado paralelo: era o
  // estado paralelo que fazia a escolha do papel morrer no salvamento — trocar
  // 80mm por 58mm mudava o preview e nada do que era gravado.
  const documento = layout.documento;
  const papel = layout.larguraPapelMm;

  const pedido = useMemo(
    () => (exemplo === "mesa" ? pedidoExemploMesa() : pedidoExemploDelivery()),
    [exemplo]
  );

  /** Toda edição passa por aqui — é o que marca o layout como não salvo. */
  const mudarLayout = (mudanca: (atual: LayoutComanda) => LayoutComanda) => {
    setSujo(true);
    setLayout(mudanca);
  };

  const trocarDocumento = (novo: DocumentoComanda) => {
    setTrocaPendente(null);
    setSujo(false);
    if (onTrocarDocumento) {
      onTrocarDocumento(novo);
      return;
    }
    setLayout(
      novo === "producao" ? layoutPadraoProducao() : layoutPadraoConferencia()
    );
  };

  const pedirTrocaDeDocumento = (novo: DocumentoComanda) => {
    if (novo === documento) return;
    // Trocar de documento troca o layout inteiro. Antes isso acontecia calado,
    // e quem tinha acabado de mexer em vinte campos perdia tudo no clique.
    if (sujo) {
      setTrocaPendente(novo);
      return;
    }
    trocarDocumento(novo);
  };

  const atualizarBloco = (indice: number, mudanca: Partial<BlocoComanda>) => {
    mudarLayout((atual) => ({
      ...atual,
      blocos: atual.blocos.map((bloco, i) =>
        i === indice ? { ...bloco, ...mudanca } : bloco
      ),
    }));
  };

  const atualizarCampo = (
    indiceBloco: number,
    indiceCampo: number,
    mudanca: Partial<CampoComanda>
  ) => {
    mudarLayout((atual) => ({
      ...atual,
      blocos: atual.blocos.map((bloco, i) =>
        i !== indiceBloco
          ? bloco
          : {
              ...bloco,
              campos: bloco.campos.map((campo, j) =>
                j === indiceCampo ? { ...campo, ...mudanca } : campo
              ),
            }
      ),
    }));
  };

  const moverBloco = (indice: number, direcao: -1 | 1) => {
    const destino = indice + direcao;
    if (destino < 0 || destino >= layout.blocos.length) return;
    mudarLayout((atual) => {
      const blocos = atual.blocos.slice();
      const [movido] = blocos.splice(indice, 1);
      blocos.splice(destino, 0, movido);
      return { ...atual, blocos };
    });
  };

  const mudarColunas = (bruto: string) => {
    setColunasDigitadas(bruto);
    const numero = Number(bruto);
    if (bruto.trim() === "" || !Number.isFinite(numero)) return;
    // Só entra no layout o que é valor válido: `larguraColunas` alimenta a
    // quebra de linha do renderizador, e 0 (ou vazio) transformaria o preview
    // em laço infinito. O que está fora da faixa fica só no campo — recortar a
    // cada tecla tornava impossível DIGITAR 32: o "3" virava 16 na hora, e o
    // "2" seguinte fazia 162, que virava 80.
    if (numero < COLUNAS_MINIMO || numero > COLUNAS_MAXIMO) return;
    const colunas = Math.round(numero);
    mudarLayout((atual) => ({ ...atual, larguraColunas: colunas }));
  };

  /** Saiu do campo: ele volta a mostrar o que de fato está no layout. */
  const encerrarEdicaoDeColunas = () => setColunasDigitadas(null);

  const salvar = async () => {
    if (!onSalvar) return;
    const gravou = await onSalvar(layout, aplicarEm);
    // `undefined` = quem chamou não informa o resultado (uso solto). Só um
    // `false` explícito mantém o "não salvo" aceso.
    if (gravou !== false) setSujo(false);
  };

  return (
    <div
      style={
        encaixado
          ? { ...estilos.container, ...estilos.containerEncaixado }
          : estilos.container
      }
    >
      <div style={estilos.cabecalho}>
        <div>
          <h2 style={estilos.titulo}>Layout de impressão</h2>
          <p style={estilos.subtitulo}>
            O que você monta aqui é exatamente o que sai na impressora do PDV.
          </p>
        </div>
        {onFechar && (
          <button style={estilos.botaoSecundario} onClick={onFechar}>
            Fechar
          </button>
        )}
      </div>

      {trocaPendente && (
        // Confirmação na própria tela: `window.confirm` some no HTML estático
        // da ferramenta de conferência e não dá para ler o que se perde.
        <div style={estilos.confirmacao}>
          <span>
            Você mexeu neste layout e ainda não salvou. Trocar para{" "}
            <b>
              {trocaPendente === "producao"
                ? "via de produção"
                : "via de conferência"}
            </b>{" "}
            descarta o que você mudou.
          </span>
          <div style={{ display: "flex", gap: "var(--sp-8)" }}>
            <button
              style={estilos.botaoSecundario}
              onClick={() => setTrocaPendente(null)}
            >
              Continuar editando
            </button>
            <button
              style={estilos.botaoPerigo}
              onClick={() => trocarDocumento(trocaPendente)}
            >
              Descartar e trocar
            </button>
          </div>
        </div>
      )}

      <div style={estilos.barraDeOpcoes}>
        <Seletor
          rotulo="Documento"
          valor={documento}
          opcoes={[
            { valor: "producao", rotulo: "Via de produção (cozinha)" },
            { valor: "conferencia", rotulo: "Via de conferência (cliente)" },
          ]}
          aoMudar={(valor) => pedirTrocaDeDocumento(valor as DocumentoComanda)}
        />
        <Seletor
          rotulo="Papel"
          valor={String(papel)}
          opcoes={PAPEIS}
          aoMudar={(valor) =>
            mudarLayout((atual) => ({
              ...atual,
              larguraPapelMm: Number(valor) as 80 | 58,
            }))
          }
        />
        <label style={estilos.seletor}>
          <span style={estilos.rotuloDoSeletor}>Colunas de texto</span>
          <input
            type="number"
            style={estilos.campoNumero}
            min={COLUNAS_MINIMO}
            max={COLUNAS_MAXIMO}
            value={colunasDigitadas ?? String(layout.larguraColunas)}
            onChange={(evento) => mudarColunas(evento.target.value)}
            onBlur={encerrarEdicaoDeColunas}
          />
        </label>
        <Seletor
          rotulo="Pedido de exemplo"
          valor={exemplo}
          opcoes={[
            { valor: "mesa", rotulo: "Mesa 12 — 4 produtos" },
            { valor: "delivery", rotulo: "Delivery — 2 produtos" },
          ]}
          aoMudar={(valor) => setExemplo(valor as "mesa" | "delivery")}
        />
      </div>

      <div style={estilos.corpo}>
        <div style={estilos.colunaEditor}>
          {layout.blocos.map((bloco, indiceBloco) => (
            <div key={`${bloco.tipo}-${indiceBloco}`} style={estilos.bloco}>
              <div style={estilos.cabecalhoDoBloco}>
                <label style={estilos.tituloDoBloco}>
                  <input
                    type="checkbox"
                    checked={bloco.ativo}
                    onChange={(evento) =>
                      atualizarBloco(indiceBloco, {
                        ativo: evento.target.checked,
                      })
                    }
                  />
                  <span>{bloco.titulo}</span>
                </label>
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    style={estilos.botaoMinusculo}
                    onClick={() => moverBloco(indiceBloco, -1)}
                    title="Subir bloco"
                  >
                    ↑
                  </button>
                  <button
                    style={estilos.botaoMinusculo}
                    onClick={() => moverBloco(indiceBloco, 1)}
                    title="Descer bloco"
                  >
                    ↓
                  </button>
                </div>
              </div>

              {bloco.tipo === "itens" && (
                <div style={estilos.avisoDoBloco}>
                  Colunas:{" "}
                  {(bloco.colunas ?? [])
                    .filter((coluna) => coluna.ativo)
                    .map((coluna) => coluna.titulo.replace("|", "").trim())
                    .join(" · ")}
                </div>
              )}

              {bloco.tipo === "observacoes" && (
                <div style={estilos.avisoDoBloco}>
                  As observações saem grudadas em cada produto. Prefixo "
                  {bloco.prefixoObservacao}" e combo "{bloco.prefixoCombo}".
                </div>
              )}

              {bloco.campos.map((campo, indiceCampo) => (
                <div key={indiceCampo} style={estilos.campo}>
                  <label style={estilos.nomeDoCampo}>
                    <input
                      type="checkbox"
                      checked={campo.ativo}
                      onChange={(evento) =>
                        atualizarCampo(indiceBloco, indiceCampo, {
                          ativo: evento.target.checked,
                        })
                      }
                    />
                    <span>{nomeDoCampo(campo)}</span>
                  </label>

                  <select
                    style={estilos.selectPequeno}
                    value={campo.alinhamento}
                    onChange={(evento) =>
                      atualizarCampo(indiceBloco, indiceCampo, {
                        alinhamento: evento.target.value as Alinhamento,
                      })
                    }
                  >
                    {(
                      ["esquerda", "centro", "direita"] as Alinhamento[]
                    ).map((valor) => (
                      <option key={valor} value={valor}>
                        {ROTULO_DO_ALINHAMENTO[valor]}
                      </option>
                    ))}
                  </select>

                  <label style={estilos.marcador} title="Negrito">
                    <input
                      type="checkbox"
                      checked={campo.negrito}
                      onChange={(evento) =>
                        atualizarCampo(indiceBloco, indiceCampo, {
                          negrito: evento.target.checked,
                        })
                      }
                    />
                    <b>N</b>
                  </label>

                  <label style={estilos.marcador} title="Largura dupla">
                    <input
                      type="checkbox"
                      checked={campo.larguraDupla}
                      onChange={(evento) =>
                        atualizarCampo(indiceBloco, indiceCampo, {
                          larguraDupla: evento.target.checked,
                        })
                      }
                    />
                    <span>2w</span>
                  </label>

                  <label style={estilos.marcador} title="Altura dupla">
                    <input
                      type="checkbox"
                      checked={campo.alturaDupla}
                      onChange={(evento) =>
                        atualizarCampo(indiceBloco, indiceCampo, {
                          alturaDupla: evento.target.checked,
                        })
                      }
                    />
                    <span>2h</span>
                  </label>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={estilos.colunaPreview}>
          <div style={estilos.tituloPreview}>Preview da impressão</div>
          <PreviewComanda
            layout={layout}
            pedido={pedido}
            larguraPapelMm={papel}
          />
        </div>
      </div>

      <div style={estilos.rodape}>
        <div style={estilos.opcoesDeAplicacao}>
          {/*
            Desligados, não escondidos: eles descrevem uma escolha que ainda não
            existe. Meios de Pagamento tem "aplicar agora / no próximo caixa"
            porque passa por acfg."FilaConfig"; o layout de comanda não entra em
            fila nenhuma — a rota grava direto em acd."ActLayout". Deixar os
            radios clicáveis prometia um agendamento que ninguém cumpre.
          */}
          <label style={{ ...estilos.opcaoDeAplicacao, ...estilos.desligado }}>
            <input
              type="radio"
              disabled
              checked={aplicarEm === "agora"}
              onChange={() => setAplicarEm("agora")}
            />
            <span>
              <b>Aplicar agora</b> — o PDV recebe em instantes
            </span>
          </label>
          <label style={{ ...estilos.opcaoDeAplicacao, ...estilos.desligado }}>
            <input
              type="radio"
              disabled
              checked={aplicarEm === "proximoPeriodo"}
              onChange={() => setAplicarEm("proximoPeriodo")}
            />
            <span>
              <b>No próximo período</b> — entra em vigor na abertura do próximo
              caixa
            </span>
          </label>
          <div style={estilos.motivo}>
            Sem escolha por enquanto: não existe fila para o layout. Salvar
            grava o desenho na Central, e é só isso que acontece.
          </div>
        </div>

        <div style={estilos.acoesDoRodape}>
          {sujo && (
            <span style={estilos.naoSalvo}>Alterações não salvas.</span>
          )}
          <div style={estilos.acaoComMotivo}>
            <button style={estilos.botaoSecundarioDesligado} disabled>
              Imprimir teste
            </button>
            <span style={estilos.motivo}>
              A Central não fala com a impressora da loja — quem tem o endereço
              da térmica é o OPS, dentro do restaurante.
            </span>
          </div>
          <button
            style={
              salvando
                ? { ...estilos.botaoPrimario, ...estilos.desligado }
                : estilos.botaoPrimario
            }
            disabled={salvando}
            onClick={salvar}
          >
            {salvando ? "Salvando…" : "Salvar layout"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Seletor: React.FC<{
  rotulo: string;
  valor: string;
  opcoes: { valor: string; rotulo: string }[];
  aoMudar: (valor: string) => void;
}> = ({ rotulo, valor, opcoes, aoMudar }) => (
  <label style={estilos.seletor}>
    <span style={estilos.rotuloDoSeletor}>{rotulo}</span>
    <select
      style={estilos.select}
      value={valor}
      onChange={(evento) => aoMudar(evento.target.value)}
    >
      {opcoes.map((opcao) => (
        <option key={opcao.valor} value={opcao.valor}>
          {opcao.rotulo}
        </option>
      ))}
    </select>
  </label>
);

const estilos: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: 20,
    background: "#f5f7f9",
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    color: "#263238",
    minHeight: "100vh",
  },
  // Dentro do painel da Configuração o editor não é mais a página: quem manda
  // na altura é o painel, e 100vh só criaria um segundo rolamento.
  containerEncaixado: {
    minHeight: 0,
  },
  cabecalho: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  titulo: { margin: 0, fontSize: 20, fontWeight: 600 },
  subtitulo: { margin: "var(--sp-4) 0 0", fontSize: 13, color: "#607d8b" },
  barraDeOpcoes: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    background: "#ffffff",
    border: "1px solid #e0e6ea",
    borderRadius: 8,
    padding: 12,
  },
  seletor: { display: "flex", flexDirection: "column", gap: 4 },
  rotuloDoSeletor: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "#78909c",
  },
  select: {
    padding: "var(--sp-6) var(--sp-8)",
    border: "1px solid #cfd8dc",
    borderRadius: 6,
    fontSize: 13,
    background: "#fff",
  },
  campoNumero: {
    padding: "var(--sp-6) var(--sp-8)",
    border: "1px solid #cfd8dc",
    borderRadius: 6,
    fontSize: "var(--fs-sm)",
    background: "#fff",
    width: "calc(var(--u) * 90)",
  },
  selectPequeno: {
    padding: "var(--sp-2) var(--sp-4)",
    border: "1px solid #cfd8dc",
    borderRadius: 4,
    fontSize: 11,
    background: "#fff",
  },
  corpo: { display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" },
  colunaEditor: {
    flex: "1 1 420px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 360,
  },
  colunaPreview: {
    flex: "0 1 480px",
    position: "sticky",
    top: 16,
    minWidth: 340,
  },
  tituloPreview: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "#78909c",
    marginBottom: 8,
  },
  bloco: {
    background: "#ffffff",
    border: "1px solid #e0e6ea",
    borderRadius: 8,
    padding: 12,
  },
  cabecalhoDoBloco: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  tituloDoBloco: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
  },
  avisoDoBloco: {
    fontSize: 11,
    color: "#78909c",
    background: "#eceff1",
    borderRadius: 4,
    padding: "var(--sp-4) var(--sp-8)",
    marginBottom: 8,
  },
  campo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "calc(var(--u) * 3) 0",
    borderTop: "1px solid #f0f3f5",
  },
  nomeDoCampo: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: 1,
    fontSize: 12,
  },
  marcador: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    fontSize: 11,
    color: "#546e7a",
  },
  botaoMinusculo: {
    border: "1px solid #cfd8dc",
    background: "#fff",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 12,
    lineHeight: 1,
    padding: "var(--sp-2) var(--sp-6)",
  },
  rodape: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    background: "#ffffff",
    border: "1px solid #e0e6ea",
    borderRadius: 8,
    padding: 12,
    flexWrap: "wrap",
  },
  opcoesDeAplicacao: { display: "flex", flexDirection: "column", gap: 4 },
  opcaoDeAplicacao: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
  },
  acoesDoRodape: {
    display: "flex",
    alignItems: "center",
    gap: "var(--sp-16)",
    flexWrap: "wrap",
  },
  acaoComMotivo: {
    display: "flex",
    alignItems: "center",
    gap: "var(--sp-8)",
    maxWidth: "calc(var(--u) * 320)",
  },
  /** Cinza e sem cursor: o controle existe, mas está desligado. */
  desligado: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  motivo: {
    fontSize: "var(--fs-2xs)",
    color: "#78909c",
    lineHeight: 1.35,
  },
  naoSalvo: {
    fontSize: "var(--fs-xs)",
    color: "#8f5e07",
    fontWeight: 600,
  },
  confirmacao: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--sp-16)",
    flexWrap: "wrap",
    background: "#fff8e1",
    border: "1px solid #ffb74d",
    borderRadius: 8,
    padding: "var(--sp-12)",
    fontSize: "var(--fs-sm)",
    color: "#7a4a00",
  },
  botaoPerigo: {
    background: "#b71c1c",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "var(--sp-8) var(--sp-16)",
    fontSize: "var(--fs-sm)",
    cursor: "pointer",
  },
  botaoSecundarioDesligado: {
    background: "#eceff1",
    color: "#90a4ae",
    border: "1px solid #cfd8dc",
    borderRadius: 6,
    padding: "var(--sp-8) calc(var(--u) * 14)",
    fontSize: "var(--fs-sm)",
    cursor: "not-allowed",
    whiteSpace: "nowrap",
  },
  botaoPrimario: {
    background: "#00695c",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "var(--sp-10) calc(var(--u) * 18)",
    fontSize: 14,
    cursor: "pointer",
  },
  botaoSecundario: {
    background: "#fff",
    color: "#37474f",
    border: "1px solid #cfd8dc",
    borderRadius: 6,
    padding: "var(--sp-8) calc(var(--u) * 14)",
    fontSize: 13,
    cursor: "pointer",
  },
};

export default EditorLayoutComanda;
